import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Anchor,
  ArrowLeft,
  ArrowRight,
  ArrowUpRight,
  Briefcase,
  Car,
  FileText,
  HardHat,
  Heart,
  Plane,
  Shield,
  TestTube2,
  Trophy,
  type LucideIcon,
} from "lucide-react";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { CTASection } from "@/components/site/CTASection";
import { PageHeroCard } from "@/components/site/PageHeroCard";
import { BookingDialog } from "@/components/site/BookingDialog";
import { CategoryBadge } from "@/components/site/CategoryBadge";
import { ModeBadges } from "@/components/site/ModeBadge";
import { Button } from "@/components/ui/button";
import { useLang } from "@/i18n/LangContext";
import { certificates, type Category, type Certificate } from "@/data/certificates";
import { certificatePathFor } from "@/data/certificateSlugs";
import { getBookingAction } from "@/data/certificateBookingActions";
import { cn } from "@/lib/utils";
import { PageMeta } from "@/components/site/PageMeta";

const categoryOrder: Category[] = [
  "korkort",
  "alkohol",
  "sjofolk",
  "arbete",
  "resor",
  "forsakring",
  "sport",
  "adoption",
  "afs",
  "ovriga",
];

const categoryIcons: Record<Category, LucideIcon> = {
  korkort: Car,
  alkohol: TestTube2,
  sjofolk: Anchor,
  arbete: Briefcase,
  resor: Plane,
  forsakring: Shield,
  sport: Trophy,
  adoption: Heart,
  afs: HardHat,
  ovriga: FileText,
};

const isCategory = (value: string | null): value is Category =>
  value !== null && (categoryOrder as string[]).includes(value);

const StartaArende = () => {
  const { t, lang, prefix } = useLang();
  const [searchParams] = useSearchParams();
  const [bookDialogOpen, setBookDialogOpen] = useState(false);
  const [selectedCert, setSelectedCert] = useState<Certificate | null>(null);
  const omradeParam = searchParams.get("omrade");
  const area = isCategory(omradeParam) ? omradeParam : null;
  const list = area ? certificates.filter((c) => c.category === area) : [];
  const homeHref = prefix || "/";
  const startHref = lang === "sv" ? "/starta-arende" : "/en/start-case";
  const allCertsHref = lang === "sv" ? "/lakarintyg" : "/en/lakarintyg";
  const selectedAction = selectedCert ? getBookingAction(selectedCert.slug) : null;

  const heroTitle = area
    ? `${t.startCase.forPrefix} ${t.categories[area]}`
    : t.startCase.title;
  const heroSubtitle = area ? t.trust.areas[area] : t.startCase.subtitle;


  const areaLabel = area ? t.categories[area] : null;
  const pageTitle = areaLabel
    ? lang === "sv"
      ? `Välj intyg – ${areaLabel} | Intygsläkaren`
      : `Choose certificate – ${areaLabel} | Intygsläkaren`
    : lang === "sv"
      ? "Hitta rätt läkarintyg | Intygsläkaren"
      : "Find the right certificate | Intygsläkaren";
  const pageDesc =
    lang === "sv"
      ? "Välj område och intygstyp. Du lämnar inga medicinska uppgifter i detta steg."
      : "Choose an area and certificate type. You do not share medical details in this step.";

  return (
    <div className="min-h-screen bg-background">
      <PageMeta title={pageTitle} description={pageDesc} />
      <Header />
      <main>
        <PageHeroCard compact>
          <div className="animate-fade-up max-w-2xl">
            {area ? (
              <Link
                to={startHref}
                className="mb-5 inline-flex items-center gap-1.5 text-sm font-medium text-white/80 transition-base hover:text-white"
              >
                <ArrowLeft className="h-4 w-4" />
                {t.startCase.back}
              </Link>
            ) : (
              <span className="inline-flex h-7 items-center justify-center rounded-full bg-white/20 px-3.5 text-xs font-semibold uppercase tracking-wider text-white backdrop-blur-sm">
                {t.startCase.eyebrow}
              </span>
            )}
            <h1 className="mt-4 text-balance font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-foreground sm:text-5xl lg:text-6xl">
              {heroTitle}
            </h1>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-muted-foreground sm:text-lg">
              {heroSubtitle}
            </p>
          </div>
        </PageHeroCard>

        {/* Category grid or cert grid */}
        <section className="py-14 lg:py-20">
          <div className="container-wide">
            {area ? (
              <>
                <div className="max-w-2xl">
                  <h2 className="font-display text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
                    {t.startCase.pickCertificate}
                  </h2>
                  <p className="mt-3 text-base leading-relaxed text-muted-foreground">
                    {t.startCase.pickCertificateHint}
                  </p>
                </div>

                <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                  {list.map((cert) => (
                    <article
                      key={cert.slug}
                      className="group relative flex flex-col overflow-hidden rounded-2xl border border-border/60 bg-card p-6 shadow-card transition-smooth hover:-translate-y-1 hover:border-primary/30 hover:shadow-elevated sm:rounded-3xl"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <CategoryBadge category={cert.category} />
                        <ModeBadges modes={cert.modes} />
                      </div>

                      <h3 className="mt-5 font-display text-xl font-semibold leading-tight text-foreground">
                        {cert.title[lang]}
                      </h3>
                      <p className="mt-2 line-clamp-3 flex-1 text-sm leading-relaxed text-muted-foreground">
                        {cert.description[lang]}
                      </p>

                      <div className="mt-5 flex items-end justify-between border-t border-border/60 pt-4">
                        <div>
                          <span className="text-[11px] uppercase tracking-wider text-muted-foreground">
                            {t.startCase.priceLabel}
                          </span>
                          <div className="font-display text-lg font-semibold text-foreground">
                            {cert.price[lang]}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button asChild variant="ghost-outline" size="sm">
                            <Link
                              to={certificatePathFor(cert.slug, lang)}
                              aria-label={t.services.readMoreAbout.replace("{title}", cert.title[lang])}
                            >
                              {t.services.readMore}
                            </Link>
                          </Button>
                          <Button
                            variant="hero"
                            size="sm"
                            onClick={() => {
                              setSelectedCert(cert);
                              setBookDialogOpen(true);
                            }}
                          >
                            {t.services.bookCta}
                            <ArrowUpRight className="ml-1 h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                    </article>
                  ))}
                </div>

                <BookingDialog
                  open={bookDialogOpen}
                  onOpenChange={(open) => {
                    setBookDialogOpen(open);
                    if (!open) setSelectedCert(null);
                  }}
                  title={selectedCert?.title[lang] ?? t.services.bookCta}
                  action={selectedAction}
                  contentKey={selectedCert?.slug}
                />
              </>
            ) : (
              <>
                <div className="max-w-2xl">
                  <h2 className="font-display text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
                    {t.startCase.pickArea}
                  </h2>
                  <p className="mt-3 text-base leading-relaxed text-muted-foreground">
                    {t.startCase.pickAreaHint}
                  </p>
                </div>

                <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {categoryOrder.map((category) => {
                    const Icon = categoryIcons[category];
                    return (
                      <Link
                        key={category}
                        to={`${startHref}?omrade=${category}`}
                        className={cn(
                          "group flex flex-col rounded-2xl border border-border/60 bg-card p-6 shadow-card sm:rounded-3xl",
                          "transition-smooth hover:-translate-y-1 hover:border-primary/30 hover:shadow-elevated"
                        )}
                      >
                        <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary transition-colors group-hover:bg-primary/15">
                          <Icon className="h-5 w-5" aria-hidden />
                        </span>
                        <div className="mt-5">
                          <span className="block text-xs font-medium uppercase tracking-wider text-muted-foreground">
                            {t.startCase.forPrefix}
                          </span>
                          <h3 className="mt-1 font-display text-xl font-semibold leading-tight text-foreground sm:text-2xl">
                            {t.categories[category]}
                          </h3>
                        </div>
                        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                          {t.trust.areas[category]}
                        </p>
                        <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-primary">
                          {lang === "sv" ? "Välj intyg" : "Choose certificate"}
                          <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
                        </span>
                      </Link>
                    );
                  })}
                </div>
              </>
            )}

            {/* Browse all strip */}
            <div className="mt-14 flex flex-col gap-4 rounded-2xl border border-border/60 bg-card px-6 py-8 shadow-card sm:rounded-3xl sm:px-8 sm:py-10 lg:flex-row lg:items-center lg:justify-between lg:gap-8">
              <div className="max-w-xl">
                <h3 className="font-display text-xl font-semibold text-foreground sm:text-2xl">
                  {t.startCase.browseAllTitle}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground sm:text-base">
                  {t.startCase.browseAllText}
                </p>
              </div>
              <Button asChild variant="ghost-outline" size="xl" className="shrink-0">
                <Link to={allCertsHref}>
                  {t.startCase.browseAllCta}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
          </div>
        </section>

        <CTASection />
        <Breadcrumbs
          items={[
            { label: lang === "sv" ? "Hem" : "Home", to: homeHref },
            {
              label: t.startCase.title,
              to: area ? startHref : undefined,
            },
            ...(area ? [{ label: t.categories[area] }] : []),
          ]}
        />
      </main>
      <Footer />
    </div>
  );
};

export default StartaArende;
